'use client';

import { useState } from 'react';
import { uploadToS3, submitPDPLRequest } from '@/apis';
import Swal from "sweetalert2";

export default function RectificationForm() {

    const rectificationFields = [
        'Name',
        'Email',
        'Phone Number',
        'Delivery Address',
        'Emirates ID / Passport Details',
        'Transaction Details (if applicable)',
        'Other (specify)',
    ];

	const [identityFile, setIdentityFile] = useState(null);
	const [supportingFiles, setSupportingFiles] = useState([]);
    const [rectifiedRows, setRectifiedRows] = useState(
        rectificationFields.map((field) => ({
            category: field,
            incorrectValue: '',
            correctValue: '',
            notes: '',
        }))
    );

	// Form fields
	const [fullName, setFullName] = useState('');
	const [email, setEmail] = useState('');
	const [phone, setPhone] = useState('');
	const [customerId, setCustomerId] = useState('');
	const [reason, setReason] = useState('');
	const [signature, setSignature] = useState('');

	// Upload Single Identity Doc
	const handleIdentityUpload = async (e) => {
		const file = e.target.files[0];
		if (!file) return;

		const formData = new FormData();
		formData.append("file", file);

		const res = await uploadToS3(formData);

		setIdentityFile({ name: file.name, url: res.fileUrl });
	};

	// Upload Multiple Supporting Docs
	const handleSupportingUpload = async (e) => {
		const files = Array.from(e.target.files);
		if (!files.length) return;

		const uploaded = [];

		for (const file of files) {
			const formData = new FormData();
			formData.append("file", file);
			const res = await uploadToS3(formData);
			uploaded.push({ name: file.name, url: res.fileUrl });
		}

		setSupportingFiles((prev) => [...prev, ...uploaded]);
	};

	const handleSubmit = async () => {
		if (!identityFile) return alert("Identity document is required.");
		if (!signature) return alert("Signature is required.");


		const payload = {
			formType: "DRR", //data rectification request

			applicantInformation: {
				fullName,
				email,
				phone,
				customerId,
			},

			identityDocuments: identityFile ? [identityFile] : [],
			supportingDocuments: supportingFiles,
            dataToBeRectified: rectifiedRows,
			requestDescription:  reason,
            accessRequestTypes:[]
	
		};

		const res = await submitPDPLRequest(payload);

        Swal.fire({
            icon: "success",
            title: "Request Submitted",
            html: `${res.message}<br>Case ID: <b style="text-blue-900">${res.caseId}</b>`,
            timer: 6000,
        });

	};

	return (
		<div className='legacy-page max-w-6xl mx-auto p-6'>
			<h1 className='text-3xl md:text-4xl font-bold text-primary mb-8'>
				Data Rectification Request Form (PDPL)
			</h1>
			<p className='text-gray-600 mb-8'>
				Request correction or update of inaccurate or incomplete personal data
				under UAE PDPL Article 6 & 15.
			</p>

        <form
            onSubmit={(e) => {
                e.preventDefault();   // stop page reload
                handleSubmit();       
            }}
        >
			{/* Section 1 */}
			<section className='mb-8'>
				<h2 className='text-xl font-medium mb-3'>Applicant Information</h2>

				<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>

					<div>
						<label className='block mb-1'>
							Full Name <span className='text-red-500'>*</span>
						</label>
						<input
							className='w-full border p-2 rounded'
							placeholder='Your full legal name'
                            type="text"
							value={fullName}
							onChange={(e) => setFullName(e.target.value)}
                            required
						/>
					</div>

					<div>
						<label className='block mb-1'>
							Registered Email Address <span className='text-red-500'>*</span>
						</label>
						<input
							className='w-full border p-2 rounded'
                            type="email"
							placeholder='Email linked to your account'
							value={email}
							onChange={(e) => setEmail(e.target.value)}
                            required
						/>
					</div>

					<div>
						<label className='block mb-1'>
							Mobile Number <span className='text-red-500'>*</span>
						</label>
						<input
							className='w-full border p-2 rounded'
							placeholder='Phone number'
                            type="phone"
							value={phone}
							onChange={(e) => setPhone(e.target.value)}
                            required
						/>
					</div>

					<div>
						<label className='block mb-1'>Customer / Employee ID</label>
						<input
							className='w-full border p-2 rounded'
							placeholder='Optional'
							value={customerId}
							onChange={(e) => setCustomerId(e.target.value)}
						/>
					</div>

				</div>
			</section>

			{/* Section 2 */}
			<section className='mb-8'>
				<h2 className='text-xl font-semibold mb-2 flex items-center gap-2'>
					Identity Verification <span className='text-red-500'>*</span>
				</h2>
				<p className='text-gray-700 mb-3'>
					To protect your data, Just Gold must verify your identity.
					<br />
					Please upload one of the following:
				</p>
				<ul className='list-disc list-inside text-gray-700 mb-4'>
					<li>Emirates ID (front + back)</li>
					<li>Passport (photo page)</li>
				</ul>

				{/* Identity Document */}
				<div>
					<label className='inline-block bg-primary text-white px-4 py-2 rounded cursor-pointer hover:bg-primary-dark transition'>
						Upload Identity Document
						<input
							type='file'
							onChange={handleIdentityUpload}
							className='hidden'
							required
						/>
					</label>

					{/* Show uploaded file name */}
					{identityFile && (
						<p className='mt-2 text-sm text-gray-700'>
							Uploaded: <span className='font-medium text-blue-900'>{identityFile.name}</span>
						</p>
					)}
				</div>
			</section>

			{/* Section 3 */}
			{rectificationFields.map((field, idx) => (
            <div key={idx} className='grid grid-cols-1 md:grid-cols-4 gap-3 mb-2'>
                <input className='border p-2 rounded' value={field} readOnly />

                <input
                className='border p-2 rounded'
                placeholder='Incorrect value'
                value={rectifiedRows[idx].incorrectValue}
                onChange={(e) =>
                    setRectifiedRows((prev) => {
                    const updated = [...prev];
                    updated[idx].incorrectValue = e.target.value;
                    return updated;
                    })
                }
                />

                <input
                className='border p-2 rounded'
                placeholder='Correct value'
                value={rectifiedRows[idx].correctValue}
                onChange={(e) =>
                    setRectifiedRows((prev) => {
                    const updated = [...prev];
                    updated[idx].correctValue = e.target.value;
                    return updated;
                    })
                }
                />

                <input
                className='border p-2 rounded'
                placeholder='Notes (optional)'
                value={rectifiedRows[idx].notes}
                onChange={(e) =>
                    setRectifiedRows((prev) => {
                    const updated = [...prev];
                    updated[idx].notes = e.target.value;
                    return updated;
                    })
                }
                />
            </div>
            ))}


			{/* Section 4 */}
			<section className='mb-8'>
				<h2 className='text-xl font-medium mb-3'>
					Supporting Documents <span className='text-red-500'>*</span>
				</h2>
				<p className='text-gray-700 mb-3'>
					Attach supporting documents proving the correct information.
				</p>

				<ul className='list-disc list-inside text-gray-700 mb-4'>
					<li>Updated Emirates ID</li>
					<li>Updated Passport</li>
					<li>Proof of Address</li>
					<li>Legal Document Supporting Name Change</li>
					<li>Bank Statement (if bank detail update is needed)</li>
				</ul>

				{/* Supporting Documents */}
				<div>
					<label className='inline-block bg-primary text-white px-4 py-2 rounded cursor-pointer hover:bg-primary-dark transition'>
						Upload Supporting Documents
						<input
							type='file'
							multiple
							onChange={handleSupportingUpload}
							className='hidden'
                            required
						/>
					</label>

					{/* Show uploaded files */}
					{supportingFiles.length > 0 && (
						<ul className='mt-2 text-sm text-blue-900 list-disc list-inside'>
							{supportingFiles.map((f, i) => (
								<li key={i}>{f.name}</li>
							))}
						</ul>
					)}
				</div>
			</section>

			{/* Section 5 */}
			<section className='mb-8'>
				<h2 className='text-xl font-medium mb-3'>Reason for Correction</h2>

				<textarea
					className='border p-2 rounded w-full'
					rows={4}
					placeholder='Why is the data incorrect or outdated?'
					value={reason}
					onChange={(e) => setReason(e.target.value)}
				/>
			</section>

			{/* Section 6 */}
			<section className='mb-8'>
				<h2 className='text-xl font-medium mb-3'>Declaration & Signature  <span className='text-red-500'>*</span></h2>

				<div className='mb-3 space-y-2'>
					<label className='flex gap-2 items-center'>
						<input type='checkbox' required />I confirm the information provided is accurate.
					</label>
					<label className='flex gap-2 items-center'>
						<input type='checkbox' required />I understand verification may be required.
					</label>
					<label className='flex gap-2 items-center'>
						<input type='checkbox' required />I understand PDPL processing timelines.
					</label>
				</div>

				<label className='block mb-1'>
					Signature <span className='text-red-500'>*</span>
				</label>
				<input
					className='w-full border p-2 rounded'
					placeholder='Type your full name as digital signature'
					value={signature}
					onChange={(e) => setSignature(e.target.value)}
					required
				/>
			</section>

			{/* Buttons */}
			<div className='flex gap-4 mt-6'>
				<button
					className='bg-primary text-white px-5 py-2 rounded'
                     type="submit"
					
				>
					Submit Rectification Request
				</button>

				<button className="bg-gray-300 px-5 py-2 rounded">Cancel</button>
			</div>

            </form>

			<section className='mb-8 mt-8'>
				<h2 className='text-xl font-medium mb-3'>
					Additional Notes (PDPL Compliance)
				</h2>

				<div className='text-sm text-gray-600'>
					• Identity verification is mandatory. <br />
					• Response time: 30 days (extendable by 60 days). <br />• All requests are logged internally.
				</div>
			</section>
		</div>
	);
}
